/**
 * VehicleActivityTimeline - Timeline de andamentos do veículo
 *
 * Cópia fiel (visual e de interação) do TaskActivityTimeline do modal de
 * tarefas, adaptada aos dados do veículo:
 * - Formulário colapsado "Adicionar andamento..." que expande com RichTextEditor
 * - Anexos staged com miniaturas + AttachmentUploadArea
 * - Cards com avatar à esquerda, nome em negrito, data à direita e ações
 * - Agrupamento por data (Hoje, Ontem, ...) com separadores
 * - Movimentações automáticas exibidas como cards do "Sistema"
 *
 * @version 2.1.0
 * RELEASE: 2026-10-09 v2.1.0 - BR-OS-028: ícone Wrench (laranja) para eventos service_order_*.
 * RELEASE: 2026-10-09 v2.0.0 - Cards no padrão da CustomerActivityTimelinePremium v7.4.0:
 *   ícone colorido por evento (sem avatar grande), título + "(por:)", selo Sistema,
 *   hora + avatar pequeno à direita, descrição com Ver mais/menos. Lógica inalterada.
 * RELEASE: 2026-08-08 v1.0.0 - Criação inicial (padronização com o modal de tarefas)
 */

import React, { useState, useCallback, useRef, useMemo } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Clock, Trash2, Pencil, Loader2, X, Plus, Check,
  FileText, Search, AlertTriangle, Flag, Briefcase, Unlink, FileSignature,
  User, ArrowRightLeft, CheckSquare, ClipboardCheck, CreditCard, RefreshCw, Wrench,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import {
  MentionPickerField,
  type MentionableMember,
} from '@/components/shared/MentionPickerField';
import { AttachmentUploadArea } from '@/components/shared/AttachmentUploadArea';
import { AttachmentThumbnail } from '@/components/shared/AttachmentThumbnail';
import { ImageLightbox, type MediaItem } from '@/components/tenant/chat/ImageLightbox';
// Resolve o tipo de mídia do preview interno (imagem/vídeo/documento)
import { resolvePreviewMediaType } from '@/lib/attachment-preview';
import { DownloadAllZipButton } from '@/hooks/useDownloadAllAsZip';
import { getMimeMediaType } from '@/lib/file-utils';
import { cn } from '@/lib/utils';
import { groupByDatePreserveOrder, DateGroup } from '@/lib/utils/group-by-date';
import { VEHICLE_AUDIT_LABELS, describeVehicleAuditEvent } from './vehicleAuditEvents';

/** v1.3.0: ações de vínculo financeiro ocultadas na timeline (já há card financeiro). */
const FINANCE_LINK_ACTIONS = new Set(['charge_linked', 'subscription_linked']);
import { useAuth } from '@/hooks/useAuth';
import { usePermissionsContext } from '@/contexts/PermissionsContext';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { useToast } from '@/hooks/use-toast';
import {
  useVehicleNotes,
  useVehicleTimeline,
  type VehicleTimelineItem,
} from '@/hooks/useVehicleDetails';

/** Props do componente */
interface VehicleActivityTimelineProps {
  /** ID do veículo */
  vehicleId: string;
  /** Só carrega quando a seção está visível */
  isActive?: boolean;
}

/** Limite de tamanho por arquivo (10MB) — mesmo padrão das tarefas */
const MAX_FILE_SIZE = 10 * 1024 * 1024;

/** Valida arquivo antes do staged */
function validateVehicleFile(file: File): { valid: boolean; error?: string } {
  if (file.size > MAX_FILE_SIZE) {
    return { valid: false, error: `${file.name} excede o limite de 10MB.` };
  }
  return { valid: true };
}

/** Rótulos amigáveis das movimentações automáticas */
const ACTION_LABELS: Record<string, string> = {
  created: 'Veículo cadastrado',
  updated: 'Dados do veículo atualizados',
  plate_lookup: 'Consulta por placa realizada',
  fines_sync: 'Multas sincronizadas',
  deal_linked: 'Vinculado a um negócio',
  deal_unlinked: 'Desvinculado de um negócio',
  contract_linked: 'Vinculado a um contrato',
  contract_unlinked: 'Desvinculado de um contrato',
  // v1.1.0 — vínculos de tarefa e de cliente/contato
  task_linked: 'Vinculado a uma tarefa',
  task_unlinked: 'Desvinculado de uma tarefa',
  customer_linked: 'Vinculado a um cliente',
  vehicle_transferred: 'Transferido para outro cliente',
  customer_unlinked: 'Desvinculado de um cliente',
  contact_linked: 'Vinculado a um contato',
  contact_unlinked: 'Desvinculado de um contato',
  note_created: 'Andamento registrado',
};

/**
 * Resolve o nome amigável da entidade citada na movimentação.
 * Procura qualquer UUID presente em `metadata` dentro do dicionário `labels`.
 */
function resolveEntityLabel(
  metadata: Record<string, unknown> | undefined,
  labels: Record<string, string>,
): string | null {
  if (!metadata) return null;
  for (const value of Object.values(metadata)) {
    if (typeof value === 'string' && labels[value]) return labels[value];
  }
  return null;
}

/** v2.0.0: meta visual (ícone + cor) por tipo de evento — paleta da timeline do Cliente */
type EventMeta = { icon: React.ComponentType<{ className?: string }>; color: string };
const C_MUTED = 'text-muted-foreground';
const C_SLATE = 'text-slate-500';
const C_AMBER = 'text-amber-600 dark:text-amber-400';
const C_INDIGO = 'text-indigo-600 dark:text-indigo-400';
const C_SKY = 'text-sky-600 dark:text-sky-400';
const C_TEAL = 'text-teal-600 dark:text-teal-400';
const VEHICLE_EVENT_ICONS: Record<string, EventMeta> = {
  created: { icon: Plus, color: C_SLATE },
  vehicle_created: { icon: Plus, color: C_SLATE },
  updated: { icon: Pencil, color: C_SLATE },
  vehicle_updated: { icon: Pencil, color: C_SLATE },
  plate_lookup: { icon: Search, color: C_AMBER },
  fines_sync: { icon: AlertTriangle, color: C_AMBER },
  vehicle_status_changed: { icon: Flag, color: C_SLATE },
  deal_linked: { icon: Briefcase, color: C_INDIGO },
  deal_unlinked: { icon: Unlink, color: C_INDIGO },
  contract_linked: { icon: FileSignature, color: C_INDIGO },
  contract_unlinked: { icon: Unlink, color: C_INDIGO },
  customer_linked: { icon: User, color: C_INDIGO },
  customer_unlinked: { icon: Unlink, color: C_INDIGO },
  contact_linked: { icon: User, color: C_INDIGO },
  contact_unlinked: { icon: Unlink, color: C_INDIGO },
  vehicle_transferred: { icon: ArrowRightLeft, color: C_INDIGO },
  task_linked: { icon: CheckSquare, color: C_AMBER },
  task_unlinked: { icon: Unlink, color: C_AMBER },
  task_status_changed: { icon: RefreshCw, color: C_AMBER },
  task_phase_changed: { icon: ArrowRightLeft, color: C_AMBER },
  inspection_created: { icon: ClipboardCheck, color: C_SKY },
  inspection_status_changed: { icon: ClipboardCheck, color: C_SKY },
  charge_unlinked: { icon: CreditCard, color: C_TEAL },
  subscription_unlinked: { icon: CreditCard, color: C_TEAL },
};

/** Resolve ícone/cor do item (nota, financeiro ou ação de auditoria) */
function resolveEventMeta(item: VehicleTimelineItem): EventMeta {
  if (item.kind === 'note') return { icon: FileText, color: C_MUTED };
  if (item.kind === 'financeiro') return { icon: CreditCard, color: C_TEAL };
  if ((item.action || '').startsWith('service_order_'))
    return { icon: Wrench, color: 'text-orange-600 dark:text-orange-400' };
  return VEHICLE_EVENT_ICONS[item.action || ''] || { icon: FileText, color: C_MUTED };
}

/** Separador de data — padrão da timeline do Cliente (pl-9 = coluna do ícone) */
function DateSeparator({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-center my-4 pl-9">
      <div className="flex-1 h-px bg-border" />
      <span className="px-3 py-0.5 text-xs font-medium text-muted-foreground bg-muted rounded-full">
        {label}
      </span>
      <div className="flex-1 h-px bg-border" />
    </div>
  );
}

/** Timeline de andamentos do veículo */
export function VehicleActivityTimeline({
  vehicleId,
  isActive = true,
}: VehicleActivityTimelineProps) {
  const { user } = useAuth();
  const { isAdmin } = usePermissionsContext();
  const { confirmDialog, ConfirmDialogComponent } = useConfirmDialog();
  const { toast } = useToast();

  const { items: rawItems, labels, isLoading } = useVehicleTimeline(vehicleId, isActive);
  // v1.3.0: vínculos de cobrança/assinatura já aparecem como card financeiro na timeline;
  // o evento de auditoria fica apenas na aba Histórico para não duplicar.
  const items = useMemo(
    () =>
      rawItems.filter(
        (it) => !(it.kind === 'activity' && FINANCE_LINK_ACTIONS.has(it.action || '')),
      ),
    [rawItems],
  );
  const { saveNote, deleteNote } = useVehicleNotes(vehicleId);

  // Estados do formulário de criação
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editorContent, setEditorContent] = useState('');
  // RELEASE: 2026-09-12 - pessoas marcadas já na criação do andamento
  const [mentionedUsers, setMentionedUsers] = useState<MentionableMember[]>([]);
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Estados de edição inline
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState('');
  const [editStagedFiles, setEditStagedFiles] = useState<File[]>([]);
  /** IDs de anexos existentes marcados para remoção durante a edição */
  const [editRemovedAttachmentIds, setEditRemovedAttachmentIds] = useState<string[]>([]);
  const editFileInputRef = useRef<HTMLInputElement>(null);

  /** Lightbox interno (preview sem sair do sistema) */
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxMediaItems, setLightboxMediaItems] = useState<MediaItem[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  /** v2.0.0: descrições expandidas (Ver mais / Ver menos) — padrão do Cliente */
  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<string>>(new Set());
  const toggleDescription = useCallback((id: string) => {
    setExpandedDescriptions((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);


  /** Seleção de arquivos (criação) */
  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      const valid: File[] = [];
      for (const file of files) {
        const validation = validateVehicleFile(file);
        if (!validation.valid) {
          toast({
            title: 'Arquivo inválido',
            description: validation.error,
            variant: 'destructive',
          });
        } else {
          valid.push(file);
        }
      }
      setStagedFiles((prev) => [...prev, ...valid]);
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [toast],
  );

  /** Seleção de arquivos (edição) */
  const handleEditFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      const valid: File[] = [];
      for (const file of files) {
        const validation = validateVehicleFile(file);
        if (!validation.valid) {
          toast({
            title: 'Arquivo inválido',
            description: validation.error,
            variant: 'destructive',
          });
        } else {
          valid.push(file);
        }
      }
      setEditStagedFiles((prev) => [...prev, ...valid]);
      if (editFileInputRef.current) editFileInputRef.current.value = '';
    },
    [toast],
  );

  /** Cria novo andamento */
  const handleSubmit = useCallback(() => {
    const plainText = editorContent.replace(/<[^>]*>/g, '').trim();
    if (!plainText) return;

    saveNote.mutate(
      {
        content: plainText,
        contentHtml: editorContent,
        files: stagedFiles,
        // RELEASE: 2026-09-12 - menções notificadas junto com o andamento
        mentionedUserIds: mentionedUsers.map((u) => u.id),
      },
      {
        onSuccess: () => {
          setEditorContent('');
          setMentionedUsers([]);
          setStagedFiles([]);
          setIsFormOpen(false);
        },
      },
    );
  }, [editorContent, stagedFiles, mentionedUsers, saveNote]);

  /** Cancela criação */
  const handleCancel = useCallback(() => {
    setEditorContent('');
    setMentionedUsers([]);
    setStagedFiles([]);
    setIsFormOpen(false);
  }, []);

  /** Inicia edição inline */
  const handleStartEdit = useCallback((item: VehicleTimelineItem) => {
    setEditingNoteId(item.id);
    setEditingContent(item.content_html || item.content || '');
    setEditStagedFiles([]);
    setEditRemovedAttachmentIds([]);
  }, []);

  /** Salva edição inline (inclui exclusão dos anexos marcados) */
  const handleSaveEdit = useCallback(() => {
    if (!editingNoteId) return;
    const plainText = editingContent.replace(/<[^>]*>/g, '').trim();
    if (!plainText) return;

    saveNote.mutate(
      {
        content: plainText,
        contentHtml: editingContent,
        noteId: editingNoteId,
        files: editStagedFiles,
        removeAttachmentIds: editRemovedAttachmentIds,
      },
      {
        onSuccess: () => {
          setEditingNoteId(null);
          setEditingContent('');
          setEditStagedFiles([]);
          setEditRemovedAttachmentIds([]);
        },
      },
    );
  }, [editingNoteId, editingContent, editStagedFiles, editRemovedAttachmentIds, saveNote]);

  /** Cancela edição inline */
  const handleCancelEdit = useCallback(() => {
    setEditingNoteId(null);
    setEditingContent('');
    setEditStagedFiles([]);
    setEditRemovedAttachmentIds([]);
  }, []);


  // Ordena do mais recente ao mais antigo e agrupa por data
  const sortedItems = useMemo(
    () =>
      [...items].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      ),
    [items],
  );
  const groupedItems = groupByDatePreserveOrder(sortedItems);

  const isSaving = saveNote.isPending;

  // Loading
  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex gap-3">
            <Skeleton className="h-8 w-8 rounded-full flex-shrink-0" />
            <div className="flex-1 space-y-2 p-3 rounded-lg border">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-3">
        {/* ============================================================ */}
        {/* Formulário de novo andamento — colapsado/expandido           */}
        {/* ============================================================ */}
        {!isFormOpen ? (
          <button
            type="button"
            onClick={() => setIsFormOpen(true)}
            className={cn(
              'w-full flex items-center gap-3 p-3 rounded-lg border border-dashed',
              'text-muted-foreground hover:text-foreground hover:border-primary/40',
              'hover:bg-muted/30 transition-all cursor-text text-left',
            )}
          >
            <SmartAvatar
              name={user?.user_metadata?.full_name || user?.email}
              src={user?.user_metadata?.avatar_url}
              size="sm"
              className="flex-shrink-0"
            />
            <span className="text-sm">Adicionar andamento...</span>
          </button>
        ) : (
          <div className="rounded-lg border bg-card shadow-sm">
            {/* Header do formulário */}
            <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/30 rounded-t-lg">
              <div className="flex items-center gap-2">
                <Plus className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">Novo Andamento</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  <X className="h-3.5 w-3.5 mr-1" />
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  className="h-7 text-xs"
                  onClick={handleSubmit}
                  disabled={isSaving || !editorContent.replace(/<[^>]*>/g, '').trim()}
                >
                  {isSaving ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                  ) : (
                    <Plus className="h-3.5 w-3.5 mr-1" />
                  )}
                  Criar
                </Button>
              </div>
            </div>

            {/* Editor rico */}
            <div className="p-2">
              <RichTextEditor
                content={editorContent}
                onChange={setEditorContent}
                placeholder="Descreva o andamento..."
                minHeight="100px"
                maxHeight="300px"
                fontSize="sm"
                showToolbar={true}
                autoFocus={true}
              />

              {/* Menções (RELEASE 2026-09-12) */}
              <div className="mt-2">
                <MentionPickerField
                  value={mentionedUsers}
                  onChange={setMentionedUsers}
                  disabled={isSaving}
                />
              </div>
            </div>

            {/* Anexos staged */}
            <div className="px-2 pb-2 space-y-2">
              {stagedFiles.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {stagedFiles.map((file, idx) => (
                    <div key={`${file.name}-${idx}`} className="relative group">
                      <div className="w-14 h-14 rounded-lg border bg-muted/30 flex items-center justify-center overflow-hidden">
                        {file.type.startsWith('image/') ? (
                          <img
                            src={URL.createObjectURL(file)}
                            alt={file.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-[9px] text-muted-foreground text-center px-1 truncate">
                            {file.name.split('.').pop()?.toUpperCase()}
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setStagedFiles((prev) => prev.filter((_, i) => i !== idx))}
                        className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center text-[10px] opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-2.5 w-2.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <AttachmentUploadArea
                onUploadClick={() => fileInputRef.current?.click()}
                isUploading={isSaving}
                maxSizeLabel="Máx: 10MB por arquivo"
              />

              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={handleFileSelect}
                accept="image/*,.pdf,.doc,.docx,.txt,.xls,.xlsx"
              />
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* Estado vazio                                                 */}
        {/* ============================================================ */}
        {sortedItems.length === 0 && (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <div className="h-12 w-12 rounded-full bg-muted/50 flex items-center justify-center mb-3">
              <Clock className="h-6 w-6 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium text-muted-foreground">
              Nenhum andamento registrado
            </p>
            <p className="text-xs text-muted-foreground/70 mt-1">
              Adicione notas e atualizações sobre este veículo
            </p>
          </div>
        )}

        {/* ============================================================ */}
        {/* Timeline agrupada por data                                   */}
        {/* ============================================================ */}
        {sortedItems.length > 0 && (
          <div className="space-y-1">
            {groupedItems.map((group: DateGroup<VehicleTimelineItem>) => (
              <div key={group.dateKey}>
                <DateSeparator label={group.dateLabel} />

                <div className="space-y-3">
                  {group.items.map((item) => {
                    // v1.2.0: eventos financeiros também são cards de "Sistema" (não editáveis)
                    const isFinanceiro = item.kind === 'financeiro';
                    const isSystem = item.kind === 'activity' || isFinanceiro;
                    const canManage =
                      !isSystem && (item.author_id === user?.id || isAdmin);
                    // v2.0.0: ícone/cor e título no padrão da timeline do Cliente
                    const meta = resolveEventMeta(item);
                    const Icon = meta.icon;
                    const title = isFinanceiro
                      ? item.entity === 'assinatura' ? 'Assinatura vinculada' : 'Cobrança vinculada'
                      : isSystem
                        ? ACTION_LABELS[item.action || ''] ||
                          VEHICLE_AUDIT_LABELS[item.action || ''] ||
                          item.action ||
                          'Movimentação'
                        : 'Andamento';

                    return (
                      <div key={`${item.kind}-${item.id}`} className="flex gap-2.5 group">
                        {/* Ícone do tipo de evento (coluna esquerda, sem fundo) */}
                        <div className="h-8 w-6 flex items-center justify-center flex-shrink-0">
                          <Icon className={cn('h-4 w-4', meta.color)} />
                        </div>

                        {/* Card do andamento — efeitos premium iguais ao Cliente */}
                        <div
                          className={cn(
                            'flex-1 min-w-0 rounded-lg border border-border/60 bg-card',
                            'shadow-sm hover:shadow-md hover:border-border transition-all duration-200',
                          )}
                        >
                          {/* Cabeçalho: título + badges à esquerda, hora/avatar/ações à direita */}
                          <div className="flex items-start justify-between gap-2 px-3 py-2">
                            <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                              <span className="text-[13px] font-semibold text-foreground">
                                {title}
                              </span>
                              {item.author_name && (
                                <span className="text-[11px] text-muted-foreground">
                                  (por: {item.author_name})
                                </span>
                              )}
                              {isSystem && (
                                <Badge variant="secondary" className="text-[9px] px-1.5 py-0 h-4">
                                  Sistema
                                </Badge>
                              )}
                            </div>

                            <div className="flex items-center gap-1 flex-shrink-0">
                              {canManage && (
                                <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5">
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6 text-muted-foreground hover:text-primary hover:bg-primary/10"
                                        onClick={() => handleStartEdit(item)}
                                      >
                                        <Pencil className="h-3.5 w-3.5" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="text-xs">
                                      Editar
                                    </TooltipContent>
                                  </Tooltip>

                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                        onClick={async () => {
                                          const confirmed = await confirmDialog({
                                            title: 'Excluir andamento?',
                                            description: 'Esta ação não pode ser desfeita.',
                                            variant: 'danger',
                                            confirmText: 'Excluir',
                                            cancelText: 'Cancelar',
                                          });
                                          if (confirmed) deleteNote.mutate(item.id);
                                        }}
                                      >
                                        <Trash2 className="h-3.5 w-3.5" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="text-xs">
                                      Excluir
                                    </TooltipContent>
                                  </Tooltip>
                                </div>
                              )}

                              <span className="text-xs text-muted-foreground whitespace-nowrap">
                                {format(new Date(item.created_at), 'HH:mm', {
                                  locale: ptBR,
                                })}
                                {item.updated_at &&
                                  new Date(item.updated_at).getTime() >
                                    new Date(item.created_at).getTime() + 1000 && (
                                    <span className="ml-1 text-[10px] text-muted-foreground/70 italic">
                                      (editado)
                                    </span>
                                  )}
                              </span>

                              {/* Avatar pequeno do responsável */}
                              {item.author_name && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div>
                                      <SmartAvatar
                                        src={item.author_avatar || undefined}
                                        name={item.author_name}
                                        size="sm"
                                        className="h-6 w-6 border border-background shadow-sm"
                                      />
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent side="left" className="text-xs">
                                    {item.author_name}
                                  </TooltipContent>
                                </Tooltip>
                              )}
                            </div>
                          </div>

                          {/* Conteúdo */}
                          <div className="px-3 pb-2">
                            {editingNoteId === item.id ? (
                              /* Modo edição inline */
                              <div className="space-y-2">
                                <RichTextEditor
                                  content={editingContent}
                                  onChange={setEditingContent}
                                  placeholder="Editar andamento..."
                                  minHeight="80px"
                                  maxHeight="250px"
                                  fontSize="sm"
                                  showToolbar={true}
                                  autoFocus={true}
                                />

                                {/* Anexos já existentes — permitem remoção durante a edição */}
                                {(item.attachments || []).filter(
                                  (att) => !editRemovedAttachmentIds.includes(att.id),
                                ).length > 0 && (
                                  <div className="flex flex-wrap gap-2">
                                    {(item.attachments || [])
                                      .filter((att) => !editRemovedAttachmentIds.includes(att.id))
                                      .map((att) => (
                                        <AttachmentThumbnail
                                          key={att.id}
                                          attachment={{
                                            id: att.id,
                                            file_name: att.file_name,
                                            file_type: att.file_type || 'file',
                                            public_url: att.public_url || '',
                                            thumbnail_url: null,
                                            file_size: att.file_size,
                                          }}
                                          onDelete={() =>
                                            setEditRemovedAttachmentIds((prev) => [...prev, att.id])
                                          }
                                        />
                                      ))}
                                  </div>
                                )}


                                {/* Novos arquivos staged */}
                                {editStagedFiles.length > 0 && (
                                  <div className="flex flex-wrap gap-2">
                                    {editStagedFiles.map((file, idx) => (
                                      <div key={`edit-staged-${idx}`} className="relative group">
                                        <div className="w-14 h-14 rounded-lg border bg-muted/30 flex items-center justify-center overflow-hidden">
                                          {file.type.startsWith('image/') ? (
                                            <img
                                              src={URL.createObjectURL(file)}
                                              alt={file.name}
                                              className="w-full h-full object-cover"
                                            />
                                          ) : (
                                            <span className="text-[9px] text-muted-foreground text-center px-1 truncate">
                                              {file.name.split('.').pop()?.toUpperCase()}
                                            </span>
                                          )}
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setEditStagedFiles((prev) =>
                                              prev.filter((_, i) => i !== idx),
                                            )
                                          }
                                          className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center text-[10px] opacity-0 group-hover:opacity-100 transition-opacity"
                                        >
                                          <X className="h-2.5 w-2.5" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                <AttachmentUploadArea
                                  onUploadClick={() => editFileInputRef.current?.click()}
                                  isUploading={isSaving}
                                  maxSizeLabel="Máx: 10MB por arquivo"
                                />
                                <input
                                  ref={editFileInputRef}
                                  type="file"
                                  multiple
                                  className="hidden"
                                  onChange={handleEditFileSelect}
                                  accept="image/*,.pdf,.doc,.docx,.txt,.xls,.xlsx"
                                />

                                <div className="flex items-center gap-1.5 justify-end">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 text-xs"
                                    onClick={handleCancelEdit}
                                    disabled={isSaving}
                                  >
                                    <X className="h-3.5 w-3.5 mr-1" />
                                    Cancelar
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="h-7 text-xs"
                                    onClick={handleSaveEdit}
                                    disabled={
                                      isSaving || !editingContent.replace(/<[^>]*>/g, '').trim()
                                    }
                                  >
                                    {isSaving ? (
                                      <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                                    ) : (
                                      <Check className="h-3.5 w-3.5 mr-1" />
                                    )}
                                    Salvar
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              /* Modo visualização — v2.0.0: padrão da timeline do Cliente */
                              isFinanceiro ? (
                                /* Evento financeiro: linha discreta com descrição · valor · situação · vencimento */
                                <p className="text-[11px] text-muted-foreground">
                                  <span className="font-medium text-foreground">
                                    {item.descricao || (item.entity === 'assinatura' ? 'Assinatura' : 'Cobrança')}
                                  </span>
                                  {typeof item.valor_total === 'number' && (
                                    <span>
                                      {' '}· {item.valor_total.toLocaleString('pt-BR', {
                                        style: 'currency',
                                        currency: 'BRL',
                                      })}
                                    </span>
                                  )}
                                  {item.status && <span> · {item.status}</span>}
                                  {(item.data_vencimento || item.proximo_vencimento) && (
                                    <span>
                                      {' '}· Vencimento{' '}
                                      {format(
                                        new Date(`${item.data_vencimento || item.proximo_vencimento}T00:00:00`),
                                        'dd/MM/yyyy',
                                      )}
                                    </span>
                                  )}
                                </p>
                              ) : isSystem ? (
                                /* Evento de sistema: título já está no cabeçalho; aqui só os detalhes */
                                (() => {
                                  const entity = resolveEntityLabel(item.metadata, labels);
                                  const detail = describeVehicleAuditEvent(item.action || '', item.metadata);
                                  const fields =
                                    item.changes && Object.keys(item.changes).length > 0
                                      ? `Campos: ${Object.keys(item.changes).join(', ')}`
                                      : null;
                                  if (!entity && !detail && !fields) return null;
                                  return (
                                    <p className="text-[11px] text-muted-foreground">
                                      {entity && <span className="font-medium text-foreground">{entity}</span>}
                                      {detail && <span>{entity ? ' · ' : ''}{detail}</span>}
                                      {fields && <span>{entity || detail ? ' · ' : ''}{fields}</span>}
                                    </p>
                                  );
                                })()
                              ) : (
                                /* Andamento manual: caixa cinza com Ver mais / Ver menos */
                                <div>
                                  <div
                                    className={cn(
                                      'bg-muted/60 rounded-md px-3 py-2 text-xs text-foreground [&_*]:text-foreground cursor-pointer',
                                      !expandedDescriptions.has(item.id) && 'line-clamp-2',
                                    )}
                                    onClick={() => toggleDescription(item.id)}
                                  >
                                    {item.content_html ? (
                                      <div
                                        className="prose prose-xs dark:prose-invert max-w-none text-xs"
                                        dangerouslySetInnerHTML={{ __html: item.content_html }}
                                      />
                                    ) : (
                                      <p className="whitespace-pre-wrap">{item.content}</p>
                                    )}
                                  </div>
                                  <button
                                    type="button"
                                    className="text-[10px] text-primary/70 hover:text-primary mt-0.5"
                                    onClick={() => toggleDescription(item.id)}
                                  >
                                    {expandedDescriptions.has(item.id) ? 'Ver menos' : 'Ver mais'}
                                  </button>
                                </div>
                              )
                            )}

                            {/* Anexos (modo visualização) — preview interno + download em zip */}
                            {editingNoteId !== item.id && (item.attachments?.length ?? 0) > 0 && (
                              <div className="mt-2">
                                <div className="flex justify-end mb-1">
                                  <DownloadAllZipButton
                                    attachments={item.attachments!.map((a) => ({
                                      id: a.id,
                                      file_name: a.file_name,
                                      public_url: a.public_url || '',
                                    })) as any}
                                    zipFileName={`anexos_andamento_${item.id.substring(0, 8)}.zip`}
                                  />
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  {item.attachments!.map((att) => (
                                    <AttachmentThumbnail
                                      key={att.id}
                                      attachment={{
                                        id: att.id,
                                        file_name: att.file_name,
                                        file_type: att.file_type || 'file',
                                        public_url: att.public_url || '',
                                        thumbnail_url: null,
                                        file_size: att.file_size,
                                      }}
                                      readOnly
                                      onClick={() => {
                                        const atts = item.attachments || [];
                                        const media: MediaItem[] = atts.map((a) => ({
                                          url: a.public_url || '',
                                          type: resolvePreviewMediaType(
                                            a.file_type, a.mime_type,
                                          ) ?? 'document',
                                          title: a.file_name,
                                        }));
                                        const idx = atts.findIndex((a) => a.id === att.id);
                                        setLightboxMediaItems(media);
                                        setLightboxIndex(idx >= 0 ? idx : 0);
                                        setLightboxOpen(true);
                                      }}
                                    />
                                  ))}
                                </div>
                              </div>
                            )}

                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox interno para visualização dos anexos dos andamentos */}
      <ImageLightbox
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        mediaItems={lightboxMediaItems}
        initialIndex={lightboxIndex}
      />



      {ConfirmDialogComponent}
    </TooltipProvider>
  );
}
